import React from "react";
import { useTranslation } from "react-i18next";
import { Identifiable, Group as G, project } from "@planzone/dto";
import {
  useProjectResourcesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveResourceGroups,
} from "@planzone/view-models";
import { useWorkspaceContext } from "@scenes/workspaceContext";

import { GroupIcon } from "@common/assets/groupIcon";
import { Loader } from "../loader";
import { Section } from "../section";
import { filterSearch } from "../utils";
import { Props } from "./types";
import { Group } from "./group";
import { extractGroupsRessource } from "./utils";
import styles from "./styles.scss";

function ResourceGroups({
  search,
  allocatedResources,
  style,
  select,
  unSelect,
  isOpen,
}: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const {
    isRequestPending: isPlanzoneRequestPending,
    isRequestSuccess: isPlanzoneRequestSuccess,
    isRequestFailure: isPlanzoneRequestFailure,
    groups,
    retrieveResourceGroups: retrievePlanzoneResourceGroups,
  } = useRetrieveResourceGroups(planzone.uuid);
  const {
    isRequestPending: isProjectRequestPending,
    isRequestSuccess: isProjectRequestSuccess,
    isRequestFailure: isProjectRequestFailure,
    groups: projectGroups,
    retrieveResourceGroups: retrieveProjectResourceGroups,
  } = useRetrieveResourceGroups(isInProject ? ctx.project.uuid : project.uuid);
  const { projectResources } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : project,
  );
  const planzoneGroups = isInProject
    ? groups.reduce<Identifiable<G>[]>((filteredGroups, group) => {
        const filteredGroup = {
          ...group,
          resources: group.resources.filter(
            ({ uuid }) =>
              !!projectResources.find(({ resource }) => resource.uuid === uuid),
          ),
        };

        return filteredGroup.resources.length > 0
          ? [...filteredGroups, filteredGroup]
          : filteredGroups;
      }, [])
    : groups;
  const selectGroups = (groups: Identifiable<G>[]) =>
    select(
      extractGroupsRessource(groups).filter(
        (resource) =>
          !allocatedResources.find(({ uuid }) => uuid === resource.uuid),
      ),
    );
  const unSelectGroups = (groups: Identifiable<G>[]) =>
    unSelect(
      extractGroupsRessource(groups).filter(
        (resource) =>
          !!allocatedResources.find(({ uuid }) => uuid === resource.uuid),
      ),
    );
  const isSelected = ({ resources, resourceType }: Identifiable<G>) =>
    resources.every(
      (resource) =>
        !!allocatedResources.find(({ uuid }) =>
          resourceType === "project"
            ? resource.resource.uuid === uuid
            : resource.uuid === uuid,
        ),
    );

  React.useEffect(() => {
    !isPlanzoneRequestPending &&
      !isPlanzoneRequestSuccess &&
      !isPlanzoneRequestFailure &&
      retrievePlanzoneResourceGroups({
        namespace: planzone.uuid,
        resourceType: "planzone",
      });
  }, []);

  React.useEffect(() => {
    !isProjectRequestPending &&
      !isProjectRequestSuccess &&
      !isProjectRequestFailure &&
      retrieveProjectResourceGroups({
        namespace: isInProject ? ctx.project.uuid : project.uuid,
        resourceType: "project",
      });
  }, []);

  return (
    <Section
      header={
        <div className={styles.header}>
          <div className={styles.iconBox}>
            <GroupIcon className={styles.icon} />
          </div>
          <p className={styles.title}>{t("common.filterMenu.groups.title")}</p>
        </div>
      }
      list={
        <div className={styles.listRoot}>
          {planzoneGroups.length > 0 && (
            <div className={styles.list}>
              {!isPlanzoneRequestPending ? (
                filterSearch(planzoneGroups, search).map((group) => (
                  <Group
                    key={group.uuid}
                    group={group}
                    isSelected={isSelected(group)}
                    select={() => selectGroups([group])}
                    unSelect={() => unSelectGroups([group])}
                  />
                ))
              ) : (
                <Loader />
              )}
            </div>
          )}
          {isInProject && projectGroups.length > 0 && (
            <div className={styles.list}>
              {!isProjectRequestPending ? (
                filterSearch(projectGroups, search).map((group) => (
                  <Group
                    key={group.uuid}
                    group={group}
                    isSelected={isSelected(group)}
                    select={() => selectGroups([group])}
                    unSelect={() => unSelectGroups([group])}
                  />
                ))
              ) : (
                <Loader />
              )}
            </div>
          )}
        </div>
      }
      style={style}
      isOpen={isOpen}
    />
  );
}

export { ResourceGroups };
